<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">{{ $t("Signup Classes") }}</h5>
          <small class="text-muted">{{
            $t("Manage signup class records and schedules")
          }}</small>
        </div>
      </div>

      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-2"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div
            class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center"
          >
            <v-select
              v-model="selectedClass"
              :options="classes"
              :clearable="true"
              :reduce="(val) => val.id"
              label="name"
              :placeholder="$t('Select ' + getLabel('class'))"
              append-to-body
              class="filter-control filter-field-item"
              @input="onClassFilterChange"
              :disabled="clsLoading"
            />
            <v-select
              v-model="selectedSection"
              :options="sections"
              :clearable="true"
              :reduce="(val) => val.id"
              label="section"
              :placeholder="$t('Select ' + getLabel('section'))"
              :disabled="!isHappiness && !selectedClass"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
            <v-select
              v-model="selectedSubject"
              :options="subjects"
              :clearable="true"
              :reduce="(val) => val.id"
              label="subject"
              :placeholder="$t('Select subject')"
              append-to-body
              class="filter-control filter-field-item"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddClassModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>{{ $t("Add Class") }}</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="records"
          :fields="fields"
          :busy="dataLoading"
          :responsive="false"
          hover
          small
          show-empty
        >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <tbody>
              <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #head()="data">
            <span>{{ $t(getLabel(data.label)) }}</span>
          </template>

          <template #cell(sno)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

          <template #cell(classname)="{ item }">
            <b-badge variant="light-primary">
              {{ (item.metadata && item.metadata.classname) || "—" }}
            </b-badge>
          </template>

          <template #cell(section)="{ item }">
            <b-badge variant="light-primary">
              {{ (item.metadata && item.metadata.section) || "—" }}
            </b-badge>
          </template>

          <template #cell(subject)="{ item }">
            <span class="font-weight-bold">
              {{ (item.metadata && item.metadata.subject) || "—" }}
            </span>
          </template>

          <template #cell(classDate)="{ item }">
            <span class="table-date-text">
              {{
                item.signupclass && item.signupclass.classDate
                  ? formatDate(item.signupclass.classDate)
                  : "—"
              }}
            </span>
          </template>

          <template #cell(time)="{ item }">
            <span class="font-small-3 text-nowrap">
              <template
                v-if="
                  item.signupclass &&
                  (item.signupclass.startTime || item.signupclass.endTime)
                "
              >
                {{ item.signupclass.startTime || "—" }} -
                {{ item.signupclass.endTime || "—" }}
              </template>
              <template v-else>—</template>
            </span>
          </template>

          <template #cell(validDates)="{ item }">
            <span class="table-date-text text-nowrap">
              <template
                v-if="
                  item.signupclass &&
                  (item.signupclass.validFrom || item.signupclass.validTill)
                "
              >
                {{ formatDate(item.signupclass.validFrom) }} -
                {{ formatDate(item.signupclass.validTill) }}
              </template>
              <template v-else>—</template>
            </span>
          </template>

          <template #cell(status)="{ item }">
            <b-badge
              :variant="
                getStatusVariant(item.signupclass && item.signupclass.status)
              "
              class="text-capitalize"
            >
              {{ (item.signupclass && item.signupclass.status) || "—" }}
            </b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ $t(data.label) }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-start">
              <b-button
                v-if="rights.view"
                variant="outline-primary"
                v-b-tooltip.hover.top
                :title="$t('View Responses')"
                class="action-btn-outline mr-50"
                :disabled="viewLoading"
                @click="viewClassDetails(item)"
              >
              <feather-icon icon="EyeIcon" />
            </b-button>
            <b-button
              v-if="!isClassDatePassed(item.signupclass && item.signupclass.classDate)"
              variant="outline-warning"
              v-b-tooltip.hover.top
              :title="$t('Send Reminder')"
              class="action-btn-outline mr-50"
              :disabled="sendingReminderId === ((item.signupclass && item.signupclass.id) || item.id)"
              @click="sendReminder(item)"
            >
              <b-spinner
                v-if="sendingReminderId === ((item.signupclass && item.signupclass.id) || item.id)"
                small
              />
              <feather-icon v-else icon="BellIcon" />
            </b-button>
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                :title="$t('Edit')"
                class="action-btn-table"
                @click="openEditClassModal(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add / Edit Signup Class Modal -->
    <b-modal
      v-model="showClassModal"
      :title="isEdit ? $t('Edit Class') : $t('Add Class')"
      centered
      size="lg"
      no-close-on-backdrop
    >
      <b-form @submit.prevent="saveSignupClass">
        <b-row>
          <!-- Class -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t(getLabel('Class'))">
              <v-select
                v-model="form.clsID"
                :options="modalClasses"
                :clearable="false"
                :reduce="(val) => val.id"
                label="name"
                :placeholder="$t('Select ' + getLabel('class'))"
                @input="onModalClassChange"
              />
            </b-form-group>
          </b-col>

          <!-- Section -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t(getLabel('Section'))">
              <v-select
                v-model="form.secID"
                :options="modalSections"
                :clearable="false"
                :reduce="(val) => val.id"
                label="section"
                :placeholder="$t('Select ' + getLabel('section'))"
              />
            </b-form-group>
          </b-col>

          <!-- Subject -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t('Subject') + ' *'">
              <v-select
                v-model="form.subID"
                :options="modalSubjects"
                :clearable="false"
                :reduce="(val) => val.id"
                label="subject"
                :placeholder="$t('Select subject')"
              />
            </b-form-group>
          </b-col>

          <!-- Class Date -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t('Class Date') + ' *'">
              <flat-pickr
                v-model="form.classDate"
                :config="dateConfig"
                class="form-control"
                :placeholder="$t('Select Class Date')"
              />
            </b-form-group>
          </b-col>

          <!-- Start Time -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t('Start Time')">
              <flat-pickr
                v-model="form.startTime"
                :config="timeConfig"
                class="form-control"
                :placeholder="$t('Select Start Time')"
              />
            </b-form-group>
          </b-col>

          <!-- End Time -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t('End Time')">
              <flat-pickr
                v-model="form.endTime"
                :config="timeConfig"
                class="form-control"
                :placeholder="$t('Select End Time')"
              />
            </b-form-group>
          </b-col>

          <!-- Valid From -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t('Valid From') + ' *'">
              <flat-pickr
                v-model="form.validFrom"
                :config="dateConfig"
                class="form-control"
                :placeholder="$t('Select Valid From')"
              />
            </b-form-group>
          </b-col>

          <!-- Valid Till -->
          <b-col md="6" cols="12" class="mb-1">
            <b-form-group :label="$t('Valid Till') + ' *'">
              <flat-pickr
                v-model="form.validTill"
                :config="dateConfig"
                class="form-control"
                :placeholder="$t('Select Valid Till')"
              />
            </b-form-group>
          </b-col>

          <!-- Description -->
          <b-col cols="12" class="mb-1">
            <b-form-group :label="$t('Description')">
              <b-form-textarea
                v-model="form.description"
                :placeholder="$t('Enter description')"
                rows="3"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>

      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="showClassModal = false"
          :disabled="saving"
        >
          {{ $t("Cancel") }}
        </b-button>
        <b-button
          variant="primary"
          @click="saveSignupClass"
          :disabled="saving"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          {{ isEdit ? $t("Update") : $t("Save") }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Class Responses Modal -->
    <b-modal
      v-model="showViewModal"
      :title="$t('Class Responses')"
      centered
      size="lg"
      scrollable
      no-close-on-backdrop
      dialog-class="signup-classes-dialog"
      @hidden="resetViewData"
    >
      <div v-if="viewLoading" class="text-center py-4">
        <b-spinner variant="primary" />
        <p class="text-muted mb-0 mt-1">{{ $t("Loading") }}...</p>
      </div>

      <template v-else-if="viewClassData">
        <div class="leave-details-section mb-1">
          <div class="section-header">
            <feather-icon icon="BookOpenIcon" size="18" class="section-icon" />
            <h6 class="section-title mb-0">{{ $t("Class Details") }}</h6>
          </div>

          <div class="details-meta-grid">
            <div class="details-meta-item">
              <span class="details-label">{{ $t(getLabel("Class")) }} & {{ $t(getLabel("Section")) }}</span>
              <span class="details-value details-value-primary">
                {{ (viewClassData.metadata && viewClassData.metadata.classname) || "—" }}
                <span class="text-muted font-weight-normal">·</span>
                {{ (viewClassData.metadata && viewClassData.metadata.section) || "—" }}
              </span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Subject") }}</span>
              <span class="details-value font-weight-bold">{{
                (viewClassData.metadata && viewClassData.metadata.subject) || "—"
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Class Date") }}</span>
              <span class="details-value d-block">
                {{
                  viewClassData.signupclass && viewClassData.signupclass.classDate
                    ? formatDate(viewClassData.signupclass.classDate)
                    : "—"
                }}
              </span>
              <div class="mt-25">
                <b-badge
                  :variant="
                    getStatusVariant(
                      viewClassData.signupclass &&
                        viewClassData.signupclass.status
                    )
                  "
                  class="text-capitalize"
                >
                  {{
                    (viewClassData.signupclass &&
                      viewClassData.signupclass.status) ||
                    "—"
                  }}
                </b-badge>
              </div>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Time") }}</span>
              <span class="details-value">
                <template
                  v-if="
                    viewClassData.signupclass &&
                    (viewClassData.signupclass.startTime ||
                      viewClassData.signupclass.endTime)
                  "
                >
                  {{ viewClassData.signupclass.startTime || "—" }} -
                  {{ viewClassData.signupclass.endTime || "—" }}
                </template>
                <template v-else>—</template>
              </span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Validity") }}</span>
              <span class="details-value">
                <template
                  v-if="
                    viewClassData.signupclass &&
                    (viewClassData.signupclass.validFrom ||
                      viewClassData.signupclass.validTill)
                  "
                >
                  {{ formatDate(viewClassData.signupclass.validFrom) }} -
                  {{ formatDate(viewClassData.signupclass.validTill) }}
                </template>
                <template v-else>—</template>
              </span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Not Responded") }}</span>
              <span class="details-value details-value-warning">{{
                classStats.pending || 0
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Attending") }}</span>
              <span class="details-value details-value-success">{{
                classStats.attending || 0
              }}</span>
            </div>
            <div class="details-meta-item">
              <span class="details-label">{{ $t("Not Attending") }}</span>
              <span class="details-value details-value-danger">{{
                classStats.notAttending || 0
              }}</span>
            </div>
          </div>

          <div
            v-if="
              viewClassData.signupclass &&
              viewClassData.signupclass.description &&
              viewClassData.signupclass.description.trim()
            "
            class="details-note-item mt-1"
          >
            <span class="details-label">{{ $t("Description") }}</span>
            <div class="details-note-content">
              {{ viewClassData.signupclass.description }}
            </div>
          </div>
        </div>

        <div class="leave-details-section leave-details-section--items mb-0">
          <div class="section-header d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center">
              <feather-icon icon="UsersIcon" size="18" class="section-icon mr-50" />
              <h6 class="section-title mb-0">{{ $t("Responses") }}</h6>
            </div>
            <div class="d-flex align-items-center">
              <b-button
                variant="outline-primary"
                size="sm"
                class="mr-50 d-flex align-items-center"
                :disabled="!classResponses.length"
                @click="printResponses"
              >
                <feather-icon icon="PrinterIcon" size="14" class="mr-50" />
                <span>{{ $t("Print") }}</span>
              </b-button>
              <b-button
                variant="outline-success"
                size="sm"
                class="d-flex align-items-center"
                :disabled="!classResponses.length"
                @click="exportResponsesExcel"
              >
                <feather-icon icon="FileTextIcon" size="14" class="mr-50" />
                <span>{{ $t("Export") }}</span>
              </b-button>
            </div>
          </div>

          <div
            v-if="!classResponses.length"
            class="text-muted text-center py-3"
          >
            {{ $t("No responses found") }}
          </div>
          <div v-else class="poll-items-table-wrap">
            <b-table
              :items="classResponses"
              :fields="viewFields"
              hover
              small
              bordered
              :responsive="false"
              class="mb-0 poll-items-table"
            >
              <template #head()="data">
                <span>{{ $t(getLabel(data.label)) }}</span>
              </template>

              <template #cell(sno)="{ index }">
                {{ index + 1 }}
              </template>

              <template #cell(student_info)="{ item }">
                <div class="d-flex align-items-center">
                  <b-avatar
                    size="32"
                    :src="getPicturePreview(item.picture)"
                    variant="light-primary"
                    class="mr-1"
                    referrerpolicy="no-referrer"
                  >
                    <feather-icon
                      v-if="!item.picture"
                      icon="UserIcon"
                      size="16"
                    />
                  </b-avatar>
                  <div>
                    <span class="font-weight-bold d-block">{{
                      item.name || "—"
                    }}</span>
                    <small v-if="item.grno" class="text-muted">
                      GR: {{ item.grno }}
                    </small>
                  </div>
                </div>
              </template>

              <template #cell(surname)="{ item }">
                <span>{{ item.surname || "—" }}</span>
              </template>

              <template #cell(date)="{ item }">
                <span class="table-date-text">{{
                  item.date ? formatDate(item.date) : "—"
                }}</span>
              </template>

              <template #cell(status)="{ item }">
                <b-badge
                  :variant="getStatusVariant(item.status)"
                  class="text-capitalize"
                >
                  {{ item.status == "pending" ? "Not Responded" : item.status || "—" }}
                </b-badge>
              </template>
            </b-table>
          </div>
        </div>
      </template>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false">
          {{ $t("Close") }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BBadge,
  BPagination,
  VBTooltip,
  BSpinner,
  BModal,
  BForm,
  BFormGroup,
  BFormTextarea,
  BAvatar,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import * as XLSX from "xlsx";
import { mapActions } from "vuex";
import Ripple from "vue-ripple-directive";
import { getMediaPreviewUrl } from "@/utils/gdriveUpload";

export default {
  name: "SignupClasses",
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BBadge,
    BPagination,
    vSelect,
    BSpinner,
    BModal,
    BForm,
    BFormGroup,
    BFormTextarea,
    BAvatar,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  data() {
    return {
      rights: {},
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      records: [],
      dataLoading: false,
      saving: false,
      isEdit: false,
      showClassModal: false,
      showViewModal: false,
      viewLoading: false,
      viewClassData: null,
      classResponses: [],
      classStats: {
        attending: 0,
        notAttending: 0,
        pending: 0,
        total: 0,
      },
      sendingReminderId: null,
      currentPage: 1,
      perPage: 50,
      totalCount: 0,
      totalPages: 1,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "30", value: 30 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      viewFields: [
        {
          key: "sno",
          label: "S.No",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "student_info",
          label: "Student",
          sortable: false,
        },
        {
          key: "surname",
          label: "Surname",
          sortable: false,
        },
        {
          key: "date",
          label: "Response On",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        },
        {
          key: "status",
          label: "Status",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        },
      ],
      classesData: [],
      classes: [],
      allSections: [],
      sections: [],
      modalSections: [{ section: "All", id: 0 }],
      subjects: [],
      clsLoading: false,
      selectedClass: null,
      selectedSection: null,
      selectedSubject: null,
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      timeConfig: {
        enableTime: true,
        noCalendar: true,
        altFormat: "h:i K",
        altInput: true,
        dateFormat: "h:i K",
      },
      form: {
        id: 0,
        title: "",
        description: "",
        clsID: 0,
        secID: 0,
        subID: null,
        ageCategory: "",
        empowerLevel: "",
        classDate: null,
        startTime: "",
        endTime: "",
        validFrom: null,
        validTill: null,
        status: "active",
        classIDs: "",
        sectionIDs: "",
        subjectIDs: "",
        timetableID: 0,
        campusID: 0,
        isDeleted: false,
      },
    };
  },
  computed: {
    isHappiness() {
      return this.reportDomain === "myskoolhappinessfoundation";
    },
    fields() {
      return [
        {
          key: "sno",
          label: "S.No",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          key: "classname",
          label: this.getLabel("Class"),
          sortable: false,
        },
        {
          key: "section",
          label: this.getLabel("Section"),
          sortable: false,
        },
        {
          key: "subject",
          label: "Subject",
          sortable: false,
        },
        {
          key: "classDate",
          label: "Class Date",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        },
        {
          key: "time",
          label: "Time",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        },
        {
          key: "validDates",
          label: "Validity",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        },
        {
          key: "status",
          label: "Status",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        },
        {
          key: "actions",
          label: "Actions",
          thClass: "text-center",
          tdClass: "text-center",
        },
      ];
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    modalClasses() {
      return [{ name: "All", id: 0 }, ...(this.classes || [])];
    },
    modalSubjects() {
      return this.subjects || [];
    },
  },
  created() {
    this.rights = this.$store.state.rights[this.$route.name] || {
      view: true,
      add: true,
      edit: true,
      delete: true,
    };

    this.loadClasses();
    this.loadSubjects();
    if (this.isHappiness) {
      this.loadAllSections();
    }
    this.loadSignupClasses();
  },
  methods: {
    ...mapActions({ get: "get", post: "post" }),

    getLabel(key) {
      if (!key) return "";
      const isHappiness = this.isHappiness;
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    getInitialForm() {
      return {
        id: 0,
        title: "",
        description: "",
        clsID: 0,
        secID: 0,
        subID: null,
        ageCategory: "",
        empowerLevel: "",
        classDate: null,
        startTime: "",
        endTime: "",
        validFrom: null,
        validTill: null,
        status: "active",
        classIDs: "",
        sectionIDs: "",
        subjectIDs: "",
        timetableID: 0,
        campusID: this.$store.state.userData.cId || 0,
        isDeleted: false,
      };
    },

    async loadClasses() {
      this.clsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
          context: this,
        };
        const response = await this.get(obj);
        const data =
          response && response.data !== undefined ? response.data : response;
        this.classesData = Array.isArray(data) ? data : [];
        this.classes = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      } finally {
        this.clsLoading = false;
      }
    },

    async loadAllSections() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Sections?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
          context: this,
        };
        const res = await this.get(obj);
        const secList = (Array.isArray(res) ? res : [])
          .filter((el) => el && el.id !== undefined && el.section != null)
          .map((sec) => ({
            id: sec.id,
            section: sec.section || sec.name || `Section ${sec.id}`,
          }));
        this.allSections = secList;
        this.sections = secList;
        this.modalSections = [{ section: "All", id: 0 }, ...secList];
      } catch (e) {
        console.error("Failed to load sections:", e);
        this.allSections = [];
        this.sections = [];
        this.modalSections = [{ section: "All", id: 0 }];
      }
    },

    loadSectionsFromClass(classId) {
      if (this.isHappiness) {
        this.sections = this.allSections;
        return;
      }
      if (!classId) {
        this.sections = [];
        return;
      }
      const selectedClassData = this.classesData.find(
        (item) => item.cls && item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        this.sections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section || sec.name || `Section ${sec.id}`,
        }));
      } else {
        this.sections = [];
      }
    },

    loadModalSectionsFromClass(classId) {
      if (this.isHappiness) {
        this.modalSections = [{ section: "All", id: 0 }, ...this.allSections];
        return;
      }
      if (!classId || classId === 0) {
        this.modalSections = [{ section: "All", id: 0 }];
        return;
      }
      const selectedClassData = this.classesData.find(
        (item) => item.cls && item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        const secList = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section || sec.name || `Section ${sec.id}`,
        }));
        this.modalSections = [{ section: "All", id: 0 }, ...secList];
      } else {
        this.modalSections = [{ section: "All", id: 0 }];
      }
    },

    onModalClassChange() {
      if (!this.isHappiness) {
        this.form.secID = 0;
        this.loadModalSectionsFromClass(this.form.clsID);
      }
    },

    async loadSubjects() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
          context: this,
        };
        const res = await this.get(obj);
        this.subjects = Array.isArray(res) ? res : [];
      } catch (e) {
        console.error("Failed to load subjects:", e);
        this.subjects = [];
      }
    },

    onClassFilterChange() {
      if (!this.isHappiness) {
        this.selectedSection = null;
        this.sections = [];
        if (this.selectedClass) {
          this.loadSectionsFromClass(this.selectedClass);
        }
      }
      this.currentPage = 1;
      this.loadSignupClasses();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadSignupClasses();
    },

    buildLoadUrl() {
      let url =
        this.$store.state.domain +
        "SignupClasses/LoadAdmin?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&page=" +
        this.currentPage +
        "&pageSize=" +
        this.perPage;

      if (this.selectedClass != null) {
        url += "&classID=" + this.selectedClass;
      }
      if (this.selectedSection != null) {
        url += "&sectionID=" + this.selectedSection;
      }
      if (this.selectedSubject != null) {
        url += "&subjectID=" + this.selectedSubject;
      }

      return url;
    },

    async loadSignupClasses() {
      this.dataLoading = true;
      try {
        const obj = {
          url: this.buildLoadUrl(),
          token: this.$store.state.userData.token,
          returnData: true,
          context: this,
        };
        const response = await this.get(obj);
        if (response === false) {
          this.records = [];
          this.totalCount = 0;
          this.$bvToast.toast("Failed to load signup classes.", {
            title: "Error",
            variant: "danger",
            solid: true,
          });
          return;
        }

        const payload =
          response && typeof response === "object" ? response : {};
        const container = payload.data !== undefined ? payload.data : payload;
        let records = [];
        if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        } else if (Array.isArray(payload.data)) {
          records = payload.data;
        }

        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount,
          page: payload?.page ?? container?.page,
          pageSize: payload?.pageSize ?? container?.pageSize,
          totalPages: payload?.totalPages ?? container?.totalPages,
        };

        this.records = records || [];
        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalFromApi = Number(meta.totalCount);
        const totalRecords =
          !Number.isNaN(totalFromApi) && totalFromApi >= 0
            ? totalFromApi
            : records.length;
        this.totalCount = totalRecords;
        if (!Number.isNaN(Number(meta.page)) && Number(meta.page) > 0) {
          this.currentPage = Number(meta.page);
        }
        this.perPage = pageSizeFromApi;
        this.totalPages =
          Number(meta.totalPages) ||
          Math.max(1, Math.ceil(this.totalCount / (this.perPage || 1)));
      } catch (e) {
        console.error("Load signup classes error:", e);
        this.records = [];
        this.totalCount = 0;
        this.$bvToast.toast("Failed to load signup classes.", {
          title: "Error",
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-UK", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return dateString;
      }
    },

    isClassDatePassed(classDate) {
      if (!classDate) return true;
      try {
        const target = new Date(classDate);
        if (isNaN(target.getTime())) return true;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        target.setHours(0, 0, 0, 0);
        return target.getTime() < today.getTime();
      } catch (e) {
        return true;
      }
    },

    async sendReminder(item) {
      const signupClassID =
        (item.signupclass && item.signupclass.id) || item.id || 0;
      if (!signupClassID) return;

      const confirmed = await this.$bvModal.msgBoxConfirm(
        this.$t(
          "Are you sure you want to send a reminder to students who have not responded yet?"
        ),
        {
          title: this.$t("Send Reminder"),
          size: "sm",
          buttonSize: "sm",
          okVariant: "primary",
          okTitle: this.$t("Yes, Send"),
          cancelTitle: this.$t("Cancel"),
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );

      if (!confirmed) return;

      this.sendingReminderId = signupClassID;
      try {
        const url =
          this.$store.state.domain +
          "SignupClasses/SendReminder?db=" +
          this.$store.state.userData.db;

        const body = {
          signupClassID: signupClassID,
          message: "",
          userID: this.$store.state.userData.userID,
        };

        await this.post({
          url,
          body,
          message: "Reminder sent successfully!",
          token: this.$store.state.userData.token,
          context: this,
        });
      } catch (error) {
        console.error("Send reminder error:", error);
        this.$bvToast.toast(this.$t("Failed to send reminder."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.sendingReminderId = null;
      }
    },

    getStatusVariant(status) {
      if (!status) return "light-secondary";
      const s = String(status).toLowerCase().trim();
      if (s === "active" || s === "published" || s === "attending") return "light-success";
      if (
        s === "inactive" ||
        s === "disabled" ||
        s === "expired" ||
        s === "not attending" ||
        s === "notattending" ||
        s === "not-attending" ||
        s === "rejected"
      )
        return "light-danger";
      if (s === "pending" || s === "draft") return "light-warning";
      return "light-primary";
    },

    openAddClassModal() {
      this.isEdit = false;
      this.form = this.getInitialForm();
      if (this.isHappiness) {
        this.modalSections = [{ section: "All", id: 0 }, ...this.allSections];
      } else {
        this.modalSections = [{ section: "All", id: 0 }];
      }
      this.showClassModal = true;
    },

    openEditClassModal(item) {
      this.isEdit = true;
      const rawObj = item.signupclass || item;
      this.form = JSON.parse(JSON.stringify(rawObj));
      if (this.form.classDate && typeof this.form.classDate === "string") {
        this.form.classDate = this.form.classDate.split("T")[0];
      }
      if (this.form.validFrom && typeof this.form.validFrom === "string") {
        this.form.validFrom = this.form.validFrom.split("T")[0];
      }
      if (this.form.validTill && typeof this.form.validTill === "string") {
        this.form.validTill = this.form.validTill.split("T")[0];
      }
      if (this.isHappiness) {
        this.modalSections = [{ section: "All", id: 0 }, ...this.allSections];
      } else if (this.form.clsID) {
        this.loadModalSectionsFromClass(this.form.clsID);
      } else {
        this.modalSections = [{ section: "All", id: 0 }];
      }
      this.showClassModal = true;
    },

    async saveSignupClass() {
      if (!this.form.subID) {
        this.$bvToast.toast(this.$t("Please select subject"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (!this.form.classDate) {
        this.$bvToast.toast(this.$t("Please select class date"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (!this.form.validFrom) {
        this.$bvToast.toast(this.$t("Please select valid from date"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (!this.form.validTill) {
        this.$bvToast.toast(this.$t("Please select valid till date"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
        return;
      }

      if (new Date(this.form.validFrom) > new Date(this.form.validTill)) {
        this.$bvToast.toast(
          this.$t("Valid from date must be earlier than valid till date"),
          {
            title: this.$t("Error!"),
            variant: "danger",
            toaster: "b-toaster-top-center",
          }
        );
        return;
      }

      this.saving = true;
      try {
        const url =
          this.$store.state.domain +
          "SignupClasses/SaveData?db=" +
          this.$store.state.userData.db +
          "&userID=" +
          this.$store.state.userData.userID;

        const response = await this.post({
          url,
          body: this.form,
          message: this.isEdit
            ? "Class updated successfully."
            : "Class added successfully.",
          token: this.$store.state.userData.token,
          context: this,
        });

        if (response) {
          this.showClassModal = false;
          this.loadSignupClasses();
        }
      } catch (error) {
        console.error("Save signup class error:", error);
        this.$bvToast.toast(this.$t("Failed to save class."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.saving = false;
      }
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadSignupClasses();
    },

    onPageChange() {
      this.loadSignupClasses();
    },

    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },

    async viewClassDetails(item) {
      this.viewClassData = item;
      this.classResponses = [];
      this.classStats = {
        attending: 0,
        notAttending: 0,
        pending: 0,
        total: 0,
      };
      this.showViewModal = true;
      this.viewLoading = true;

      try {
        const signupClassID =
          (item.signupclass && item.signupclass.id) || item.id || 0;
        const obj = {
          url:
            this.$store.state.domain +
            "SignupClasses/LoadDetails?db=" +
            this.$store.state.userData.db +
            "&signupClassID=" +
            signupClassID,
          token: this.$store.state.userData.token,
          context: this,
        };
        const response = await this.get(obj);
        const resData =
          response && response.data !== undefined ? response.data : response;
        if (resData && typeof resData === "object" && !Array.isArray(resData)) {
          this.classResponses = Array.isArray(resData.responses)
            ? resData.responses
            : [];
          this.classStats = resData.stats || {
            attending: 0,
            notAttending: 0,
            pending: 0,
            total: 0,
          };
        } else if (Array.isArray(resData)) {
          this.classResponses = resData;
          this.classStats = {
            attending: 0,
            notAttending: 0,
            pending: 0,
            total: resData.length,
          };
        } else {
          this.classResponses = [];
          this.classStats = {
            attending: 0,
            notAttending: 0,
            pending: 0,
            total: 0,
          };
        }
      } catch (error) {
        console.error("Error loading class details:", error);
        this.classResponses = [];
        this.classStats = {
          attending: 0,
          notAttending: 0,
          pending: 0,
          total: 0,
        };
        this.$bvToast.toast(this.$t("Failed to load responses."), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      } finally {
        this.viewLoading = false;
      }
    },

    resetViewData() {
      this.viewClassData = null;
      this.classResponses = [];
      this.classStats = {
        attending: 0,
        notAttending: 0,
        pending: 0,
        total: 0,
      };
      this.viewLoading = false;
    },

    printResponses() {
      if (!this.classResponses || !this.classResponses.length) return;

      const classData = this.viewClassData || {};
      const metadata = classData.metadata || {};
      const signupClass = classData.signupclass || {};

      const className = metadata.classname || "—";
      const sectionName = metadata.section || "—";
      const subjectName = metadata.subject || "—";
      const classDate = signupClass.classDate ? this.formatDate(signupClass.classDate) : "—";
      const timeStr =
        signupClass.startTime || signupClass.endTime
          ? `${signupClass.startTime || "—"} - ${signupClass.endTime || "—"}`
          : "—";

      const rowsHtml = this.classResponses
        .map((item, index) => {
          const studentName = item.name || "—";
          const grText = item.grno ? ` <span style="color:#64748b;font-size:11px;">(GR: ${item.grno})</span>` : "";
          const surname = item.surname || "—";
          const date = item.date ? this.formatDate(item.date) : "—";
          const status = item.status || "—";

          return `
            <tr>
              <td style="text-align:center;padding:6px 8px;border:1px solid #cbd5e1;">${index + 1}</td>
              <td style="padding:6px 8px;border:1px solid #cbd5e1;font-weight:600;">${studentName}${grText}</td>
              <td style="padding:6px 8px;border:1px solid #cbd5e1;">${surname}</td>
              <td style="text-align:center;padding:6px 8px;border:1px solid #cbd5e1;">${date}</td>
              <td style="text-align:center;padding:6px 8px;border:1px solid #cbd5e1;text-transform:capitalize;">${status}</td>
            </tr>
          `;
        })
        .join("");

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Class Responses</title>
          <style>
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              color: #1e293b;
              margin: 20px;
              padding: 0;
            }
            .header {
              border-bottom: 2px solid #7c5dfa;
              padding-bottom: 10px;
              margin-bottom: 15px;
            }
            .title {
              font-size: 20px;
              font-weight: bold;
              color: #2d1b69;
              margin: 0 0 6px 0;
            }
            .meta-grid {
              display: grid;
              grid-template-columns: repeat(4, 1fr);
              gap: 8px;
              margin-bottom: 15px;
              font-size: 12px;
              background: #f8fafc;
              padding: 10px;
              border-radius: 6px;
              border: 1px solid #e2e8f0;
            }
            .meta-item strong {
              color: #475569;
              display: block;
              font-size: 10px;
              text-transform: uppercase;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 12px;
            }
            th {
              background: #f1f5f9;
              color: #2d1b69;
              font-weight: bold;
              padding: 8px;
              border: 1px solid #cbd5e1;
              text-align: left;
            }
            th.text-center {
              text-align: center;
            }
            @media print {
              .no-print {
                display: none !important;
              }
              body {
                margin: 0;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1 class="title">Class Responses</h1>
          </div>
          <div class="meta-grid">
            <div class="meta-item">
              <strong>${this.getLabel("Class")} & ${this.getLabel("Section")}</strong>
              <span>${className} · ${sectionName}</span>
            </div>
            <div class="meta-item">
              <strong>Subject</strong>
              <span>${subjectName}</span>
            </div>
            <div class="meta-item">
              <strong>Class Date</strong>
              <span>${classDate}</span>
            </div>
            <div class="meta-item">
              <strong>Time</strong>
              <span>${timeStr}</span>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th class="text-center" style="width:40px;">#</th>
                <th>Student</th>
                <th>Surname</th>
                <th class="text-center">Responses On</th>
                <th class="text-center">Status</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
        </html>
      `;

      const iframe = document.createElement("iframe");
      iframe.setAttribute(
        "style",
        "position:fixed;width:0;height:0;border:0;visibility:hidden;"
      );
      document.body.appendChild(iframe);
      const doc = iframe.contentWindow.document;
      doc.open();
      doc.write(htmlContent);
      doc.close();
      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => iframe.remove(), 1000);
        }
      }, 250);
    },

    exportResponsesExcel() {
      if (!this.classResponses || !this.classResponses.length) return;

      const exportData = this.classResponses.map((item, index) => ({
        "#": index + 1,
        Student: item.name || "—",
        Surname: item.surname || "—",
        "Responses On": item.date ? this.formatDate(item.date) : "—",
        Status: item.status || "—",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Responses");

      const className =
        (this.viewClassData &&
          this.viewClassData.metadata &&
          this.viewClassData.metadata.classname) ||
        this.getLabel('Class');
      const subjectName =
        (this.viewClassData &&
          this.viewClassData.metadata &&
          this.viewClassData.metadata.subject) ||
        "";
      const fileName = `Signup_Class_Responses_${className}_${subjectName}_${new Date().getTime()}.xlsx`
        .replace(/\s+/g, "_")
        .replace(/[^\w.-]/g, "");

      XLSX.writeFile(workbook, fileName || "Signup_Class_Responses.xlsx");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../planner/plannerListTable.scss";

::v-deep .planner-list-table {
  th.col-title,
  td.col-title {
    white-space: normal !important;
    word-break: break-word;
    overflow-wrap: anywhere;
    vertical-align: top;
    min-width: 150px;
    max-width: 260px;
  }
}

.table-title-text {
  display: block;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.45;
  font-weight: 600;
  color: #2d1b69;
}

.table-date-text {
  white-space: nowrap;
  font-size: 0.8125rem;
  color: #475569;
}

.action-btn-outline {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.35rem 0.5rem;
  border-radius: 8px;
}

.leave-details-section {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 12px;
  padding: 0.75rem 1rem;
  box-shadow: 0 2px 8px rgba(115, 103, 240, 0.05);
  position: relative;

  &--items {
    overflow: visible;
  }

  &:not(.leave-details-section--items) {
    overflow: hidden;
  }

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
  }

  .section-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-bottom: 0.6rem;
    padding-bottom: 0.4rem;
    border-bottom: 1.5px solid #f0edff;
  }

  .section-icon {
    color: #7c5dfa;
    stroke-width: 2.5;
  }

  .section-title {
    font-size: 0.9rem;
    font-weight: 800;
    color: #2d1b69;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
}

.details-meta-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.5rem;
}

.details-meta-item {
  background: linear-gradient(180deg, #ffffff 0%, #f8f7ff 100%);
  border-radius: 8px;
  padding: 0.45rem 0.65rem;
  border: 1px solid #f0edff;
  box-shadow: 0 1px 4px rgba(99, 102, 241, 0.03);

  &--wide {
    grid-column: 1 / -1;
  }

  .details-label {
    display: block;
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 0.5px;
    font-weight: 700;
    margin-bottom: 0.2rem;
  }

  .details-value {
    display: block;
    font-size: 0.85rem;
    font-weight: 700;
    color: #2d1b69;
    line-height: 1.25;

    &.details-value-primary {
      color: #7c5dfa;
    }

    &.details-value-info {
      color: #06b6d4;
    }

    &.details-value-success {
      color: #10b981;
    }

    &.details-value-danger {
      color: #ea5455;
    }

    &.details-value-warning {
      color: #ff9f43;
    }
  }
}

.details-note-item {
  .details-label {
    display: block;
    font-size: 0.65rem;
    text-transform: uppercase;
    color: #8a86b7;
    letter-spacing: 0.5px;
    font-weight: 700;
    margin-bottom: 0.2rem;
  }
}

.details-note-content {
  background: #f8f9fa;
  border: 1px solid #ebe9f1;
  border-radius: 6px;
  padding: 0.45rem 0.65rem;
  font-size: 0.8125rem;
  color: #4b4b4b;
  line-height: 1.4;
  white-space: pre-wrap;
}

.poll-items-table-wrap {
  overflow-x: auto;
}

.poll-items-table {
  thead th {
    background: #f8f7ff !important;
    color: #2d1b69 !important;
    font-weight: 700;
    border-color: #e0d8ff !important;
    white-space: nowrap;
  }

  tbody td {
    vertical-align: middle;
    border-color: #f0edff !important;
  }
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.signup-classes-dialog {
  max-width: 94% !important;
  width: 94% !important;
  margin-left: auto !important;
  margin-right: auto !important;

  @media (min-width: 992px) {
    max-width: 80% !important;
    width: 80% !important;
  }

  @media (min-width: 1200px) {
    max-width: 70% !important;
    width: 70% !important;
  }

  @media (min-width: 1400px) {
    max-width: 60% !important;
    width: 60% !important;
  }
}
</style>
